import { DateTime } from "luxon";
import { Nullable, Optional } from "sonobello.utilities.react";

import { Customer, EnhancedCustomer } from "../../../dtos/Customer";
import { DisqualifyingReason, DisqualifyingReasonsType } from "../../../dtos/DisqualifyingReason";
import { ICalendarService } from "../../Calendar/Types/ICalendar";
import AllMedicalQuestionsEvaluator from "../../Steps/Medical/Compositions/AllMedicalQuestionsEvaluator";
import IMedicalConclusions from "../../Steps/Medical/Types/IMedicalConclusions";
import IMedicalQualification from "../../Steps/Medical/Types/IMedicalQualification";
import { ReservationCenter } from "../../Types/IReservation";
import INoteTag from "./INoteTag";

const serviceSubCategoryName: INoteTag<{
  reservation: Nullable<{ service: Pick<ICalendarService, "serviceSubCategoryName"> }>;
}> = {
  buildTagContent: ({ reservation }) => reservation?.service.serviceSubCategoryName || null
};

const obxTag: INoteTag<unknown> = { buildTagContent: () => "OBX" };

const businessUnitTag: INoteTag<{
  reservation: Nullable<{ service: Pick<ICalendarService, "businessUnit"> }>;
}> = {
  buildTagContent: ({ reservation }) => reservation?.service.businessUnit.name || null
};

const customerHeightWeightBmiTag: INoteTag<{ customer: Pick<Customer, "height" | "weight"> }> = {
  buildTagContent: ({ customer }) => {
    const tokens: string[] = [];
    if (customer.height) tokens.push(`${customer.height.feet}ft${customer.height.inches}in`);
    if (customer.weight) tokens.push(`${customer.weight}lbs`);
    if (customer.height && customer.weight)
      tokens.push(`${Customer.getBmi(customer as Parameters<typeof Customer.getBmi>[0])}BMI`);
    return tokens.join(", ") || null;
  }
};

const customerDateOfBirthNoteTag: INoteTag<{ customer: Pick<Customer, "dateOfBirth"> }> = {
  buildTagContent: ({ customer }) =>
    customer.dateOfBirth ? DateTime.fromISO(customer.dateOfBirth).toLocaleString(DateTime.DATE_MED) : null
};

const customerMedicalQualificationPassFailNoteTag: INoteTag<{
  customer: Pick<Customer, "height" | "weight" | "dateOfBirth">;
  medicalForm: IMedicalQualification;
}> = {
  buildTagContent: ({ customer, medicalForm }) => {
    if (!medicalForm) return null;
    let disqualifyingReason: Optional<DisqualifyingReasonsType> = undefined;
    if (customer.height && customer.weight && customer.dateOfBirth)
      disqualifyingReason = Customer.getDisqualifyingReason(
        customer as Parameters<typeof Customer.getDisqualifyingReason>[0]
      );

    if (disqualifyingReason || AllMedicalQuestionsEvaluator.isNonCandidate(medicalForm)) return "MQ Fail";

    if (!disqualifyingReason && !AllMedicalQuestionsEvaluator.isComplete(medicalForm)) return null;

    return "MQ Pass";
  }
};

const customerMedicalQualificationTimestampedNoteTag: INoteTag<{ customer: Pick<Customer, "qualificationDateUtc"> }> = {
  buildTagContent: ({ customer }) =>
    `MQ Pass ${
      customer.qualificationDateUtc
        ? DateTime.fromISO(customer.qualificationDateUtc, { zone: "UTC" }).toLocaleString(DateTime.DATE_MED)
        : DateTime.now().toLocaleString(DateTime.DATE_MED)
    }`
};

const customerDistanceToCenterNoteTag: INoteTag<{ customer: Pick<EnhancedCustomer, "distanceToCenter"> }> = {
  buildTagContent: ({ customer }) => {
    const distance = customer.distanceToCenter;
    return distance !== undefined ? `${distance.toFixed(2)} mi` : distance || null;
  }
};

const customerDistanceToReservationCenterNoteTag: INoteTag<{
  reservation: { center: Pick<ReservationCenter, "distance"> };
}> = {
  buildTagContent: ({
    reservation: {
      center: { distance }
    }
  }) => {
    return distance !== null ? `${distance.toFixed(2)} mi` : distance || null;
  }
};

const centerNameNoteTag: INoteTag<{ center: Pick<{ name: string }, "name"> }> = {
  buildTagContent: ({ center }) => center.name
};

const disqualifyingReasonsNoteTag: INoteTag<{
  disqualifyingReasons: DisqualifyingReason[];
  customer: Pick<Customer, "height" | "weight" | "dateOfBirth">;
}> = {
  buildTagContent: ({ disqualifyingReasons, customer }) => {
    {
      if (!customer.height || !customer.weight || !customer.dateOfBirth) return null;
      const customerDisqualifyingReason = Customer.getDisqualifyingReason(
        customer as Parameters<typeof Customer.getDisqualifyingReason>[0]
      );
      if (!customerDisqualifyingReason || !disqualifyingReasons) return null;
      const disqualifyingReason = DisqualifyingReason.GetReason(disqualifyingReasons, customerDisqualifyingReason);
      return `Non-Can: ${disqualifyingReason.name}`;
    }
  }
};

const diabetesNoteTag: INoteTag<{ medicalConclusions: Pick<IMedicalConclusions, "hasDiabetes"> }> = {
  buildTagContent: ({ medicalConclusions: { hasDiabetes } }) => (hasDiabetes ? "Diabetic" : null)
};

const radiationProtectedAreasNoteTag: INoteTag<{
  medicalConclusions: Pick<IMedicalConclusions, "hasRadiationProtectedAreas">;
}> = {
  buildTagContent: ({ medicalConclusions: { hasRadiationProtectedAreas } }) =>
    hasRadiationProtectedAreas ? "Radiation area restriction" : null
};

const pacemakerWithoutDefibrillatorNoteTag: INoteTag<{
  medicalConclusions: Pick<IMedicalConclusions, "hasPacemakerWithoutDefibrillator">;
}> = {
  buildTagContent: ({ medicalConclusions: { hasPacemakerWithoutDefibrillator } }) =>
    hasPacemakerWithoutDefibrillator ? "Pacemaker evaluation at consult" : null
};

const NoteTags = {
  businessUnitTag,
  centerNameNoteTag,
  customerDateOfBirthNoteTag,
  customerDistanceToCenterNoteTag,
  customerDistanceToReservationCenterNoteTag,
  customerHeightWeightBmiTag,
  customerMedicalQualificationPassFailNoteTag,
  customerMedicalQualificationTimestampedNoteTag,
  diabetesNoteTag,
  disqualifyingReasonsNoteTag,
  obxTag,
  pacemakerWithoutDefibrillatorNoteTag,
  radiationProtectedAreasNoteTag,
  serviceSubCategoryName
};

export default NoteTags;
